import {
  adminGroupRulesControllerCreate,
  adminGroupRulesControllerList,
  adminGroupRulesControllerSetMembers,
  getAdminGroupRulesControllerGetOneQueryKey,
  getAdminGroupRulesControllerListQueryKey,
} from '@app/api-client';
import { adminCreateUserSchema } from '@app/contracts';
import type { AdminCreateUserInput, SiteDto } from '@app/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { fetchSites } from '../../admin-sites/api/index';
import { useAuthStore } from '../../auth/store';
import { useDemoStore } from '../../demo/store/demo.store';
import { KEY_SITE_PROGRESS_ROOT } from '../../progression/hooks/useProgression.js';
import {
  ADMIN_USERS_LIST_MAX_PAGE,
  assignUserToSite,
  createAdminUser,
  fetchAdminUsers,
} from '../api';
import { AdminGroupsPanel } from '../components/AdminGroupsPanel';
import { AdminUsersTable, USERS_KEY } from '../components/AdminUsersTable';
import { SitePicker } from '../components/SitePicker';

const ROLE_OPTIONS = [
  { value: 'member', label: 'Membre' },
  { value: 'admin', label: 'Admin' },
] as const;

function CreateUserModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AdminCreateUserInput>({
    resolver: zodResolver(adminCreateUserSchema),
    defaultValues: { role: 'member' },
  });

  const [confirmPwd, setConfirmPwd] = useState('');
  const [apiError, setApiError] = useState<string | null>(null);
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [selectedSites, setSelectedSites] = useState<Set<string>>(new Set());
  const [preferredSiteId, setPreferredSiteId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { data: allSites = [] } = useQuery<SiteDto[]>({
    queryKey: ['admin-sites'],
    queryFn: fetchSites,
  });

  const toggleSite = (id: string) => {
    setSelectedSites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        if (preferredSiteId === id) setPreferredSiteId(null);
      } else {
        next.add(id);
        if (next.size === 1) setPreferredSiteId(id);
      }
      return next;
    });
  };

  const create = useMutation({ mutationFn: createAdminUser });

  const onSubmit = handleSubmit(async (data) => {
    if (data.password !== confirmPwd) {
      setPwdError('Les mots de passe ne correspondent pas');
      return;
    }
    setPwdError(null);
    setApiError(null);
    setIsSaving(true);
    try {
      const created = await create.mutateAsync(data);
      await Promise.all(
        [...selectedSites].map((siteId) =>
          assignUserToSite(created.id, siteId, siteId === preferredSiteId),
        ),
      );
      qc.invalidateQueries({ queryKey: USERS_KEY });
      onClose();
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setIsSaving(false);
    }
  });

  const pending = isSubmitting || isSaving;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div className="w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-slate-200">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-8 py-5">
          <div>
            <p className="text-[0.65rem] font-bold uppercase tracking-[0.18em] text-teal-700">
              Administration
            </p>
            <h3 className="text-lg font-semibold text-slate-900">Créer un utilisateur</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label="Fermer"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden
            >
              <title>Fermer</title>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} className="space-y-5 px-8 py-6">
          {/* Identité */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Prénom *" error={errors.firstName?.message}>
              <input
                {...register('firstName')}
                placeholder="Jean"
                className={inputClass(!!errors.firstName)}
              />
            </Field>
            <Field label="Nom *" error={errors.lastName?.message}>
              <input
                {...register('lastName')}
                placeholder="Dupont"
                className={inputClass(!!errors.lastName)}
              />
            </Field>
          </div>

          <Field label="Alias (identifiant de connexion) *" error={errors.alias?.message}>
            <input
              {...register('alias')}
              placeholder="jean.dupont"
              className={inputClass(!!errors.alias)}
            />
            <p className="mt-1 text-[0.7rem] text-slate-400">
              Caractères autorisés : a-z, 0-9, . _ -
            </p>
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="E-mail" error={errors.email?.message}>
              <input
                {...register('email')}
                type="email"
                placeholder="jean@example.com"
                className={inputClass(!!errors.email)}
              />
            </Field>
            <Field label="Téléphone" error={errors.phone?.message}>
              <input
                {...register('phone')}
                type="tel"
                placeholder="+33 6 00 00 00 00"
                className={inputClass(!!errors.phone)}
              />
            </Field>
          </div>

          <Field label="Rôle *" error={errors.role?.message}>
            <select {...register('role')} className={inputClass(!!errors.role)}>
              {ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>

          {/* Sites */}
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="mb-3 text-[0.65rem] font-bold uppercase tracking-wider text-slate-500">
              Sites de travail
              {selectedSites.size > 0 && (
                <span className="ml-2 rounded-full bg-teal-100 px-2 py-0.5 text-[0.6rem] font-semibold text-teal-700">
                  {selectedSites.size} sélectionné{selectedSites.size > 1 ? 's' : ''}
                </span>
              )}
            </p>
            <SitePicker
              sites={allSites}
              selected={selectedSites}
              preferred={preferredSiteId}
              onToggle={toggleSite}
              onPreferred={(id: string) => setPreferredSiteId((p) => (p === id ? null : id))}
            />
          </div>

          {/* Mot de passe */}
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="mb-3 text-[0.65rem] font-bold uppercase tracking-wider text-slate-500">
              Mot de passe temporaire
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Mot de passe *" error={errors.password?.message}>
                <input
                  {...register('password')}
                  type="password"
                  placeholder="12 caractères min."
                  className={inputClass(!!errors.password)}
                />
              </Field>
              <Field label="Confirmer *" error={pwdError ?? undefined}>
                <input
                  type="password"
                  value={confirmPwd}
                  onChange={(e) => {
                    setConfirmPwd(e.target.value);
                    if (pwdError) setPwdError(null);
                  }}
                  placeholder="Idem ci-contre"
                  className={inputClass(!!pwdError)}
                />
              </Field>
            </div>
          </div>

          {apiError && (
            <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              {apiError}
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={pending}
              className="rounded-xl bg-teal-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:opacity-60"
            >
              {pending ? 'Création…' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function inputClass(hasError: boolean) {
  return `w-full rounded-xl border px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2
    ${
      hasError ? 'border-red-300 focus:ring-red-400/30' : 'border-slate-200 focus:ring-teal-400/30'
    }`;
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <span className="mb-1.5 block text-xs font-semibold text-slate-600">{label}</span>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

/* ─── Données démo ───────────────────────────────────────── */
const DEMO_USERS: AdminCreateUserInput[] = [
  {
    alias: 'demo.membre',
    firstName: 'Alice',
    lastName: 'Martin',
    email: 'alice.martin@demo.local',
    phone: undefined,
    password: 'demo-square-iso-2026',
    role: 'member',
  },
  {
    alias: 'demo.admin2',
    firstName: 'Bob',
    lastName: 'Lefebvre',
    email: 'bob.lefebvre@demo.local',
    phone: undefined,
    password: 'demo-square-iso-2026',
    role: 'admin',
  },
  {
    alias: 'demo.membre2',
    firstName: 'Claire',
    lastName: 'Durand',
    email: 'claire.durand@demo.local',
    phone: undefined,
    password: 'demo-square-iso-2026',
    role: 'member',
  },
];

const DEMO_GROUP_AUDITEURS = 'Auditeurs';
const DEMO_GROUP_REDACTEURS = 'Rédacteurs';

/** Aliases démo : auditeurs = admin + Alice ; rédacteurs = Alice + Claire. */
const DEMO_AUDITEURS_ALIASES = ['demo.admin2', 'demo.membre'] as const;
const DEMO_REDACTEURS_ALIASES = ['demo.membre', 'demo.membre2'] as const;

async function runDemoUserSeed(qc: QueryClient): Promise<{
  created: number;
  skipped: number;
}> {
  const availableSites = await fetchSites();
  let created = 0;
  let skipped = 0;
  for (const user of DEMO_USERS) {
    try {
      const createdUser = await createAdminUser(user);
      if (availableSites.length > 0) {
        await Promise.all(
          availableSites.map((site, i) => assignUserToSite(createdUser.id, site.id, i === 0)),
        );
      }
      created++;
    } catch {
      skipped++;
    }
  }
  await qc.invalidateQueries({ queryKey: USERS_KEY });
  return { created, skipped };
}

function useAdminDemoSeed(adminTab: 'users' | 'groups') {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ created: number; skipped: number } | null>(null);
  const [groupsDemoMessage, setGroupsDemoMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function seed() {
    setBusy(true);
    setResult(null);
    setGroupsDemoMessage(null);
    setError(null);
    try {
      if (adminTab === 'users') {
        const r = await runDemoUserSeed(qc);
        setResult(r);
        return;
      }

      const { created, skipped } = await runDemoUserSeed(qc);
      const usersPage = await fetchAdminUsers(ADMIN_USERS_LIST_MAX_PAGE);
      const byAlias = new Map(usersPage.data.map((u) => [u.alias, u.id]));

      const resolveIds = (aliases: readonly string[]) => {
        const ids: string[] = [];
        const missing: string[] = [];
        for (const a of aliases) {
          const id = byAlias.get(a);
          if (id) ids.push(id);
          else missing.push(a);
        }
        return { ids, missing };
      };

      const aud = resolveIds(DEMO_AUDITEURS_ALIASES);
      const red = resolveIds(DEMO_REDACTEURS_ALIASES);
      const missing = [...new Set([...aud.missing, ...red.missing])];
      if (missing.length > 0) {
        throw new Error(`Utilisateurs démo introuvables : ${missing.join(', ')}`);
      }

      let list = await adminGroupRulesControllerList();

      const ensureGroup = async (name: string) => {
        const found = list.find((g) => g.name === name);
        if (found) return found;
        const createdGroup = await adminGroupRulesControllerCreate({ name });
        list = await adminGroupRulesControllerList();
        return list.find((g) => g.id === createdGroup.id) ?? createdGroup;
      };

      const gAud = await ensureGroup(DEMO_GROUP_AUDITEURS);
      const gRed = await ensureGroup(DEMO_GROUP_REDACTEURS);

      await adminGroupRulesControllerSetMembers(gAud.id, { userIds: aud.ids });
      await adminGroupRulesControllerSetMembers(gRed.id, { userIds: red.ids });

      void qc.invalidateQueries({ queryKey: getAdminGroupRulesControllerListQueryKey() });
      void qc.invalidateQueries({ queryKey: getAdminGroupRulesControllerGetOneQueryKey(gAud.id) });
      void qc.invalidateQueries({ queryKey: getAdminGroupRulesControllerGetOneQueryKey(gRed.id) });
      void qc.invalidateQueries({ queryKey: KEY_SITE_PROGRESS_ROOT });

      setGroupsDemoMessage(
        `Groupes « ${DEMO_GROUP_AUDITEURS} » et « ${DEMO_GROUP_REDACTEURS} » mis à jour. ` +
          `${created} compte(s) créé(s)${skipped > 0 ? `, ${skipped} déjà présents` : ''}.`,
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Erreur lors de la préparation de la démonstration',
      );
    } finally {
      setBusy(false);
    }
  }

  return { seed, busy, result, groupsDemoMessage, error };
}

function DemoSeedUsersButton({
  onSeed,
  busy,
  tab,
}: {
  onSeed: () => void;
  busy: boolean;
  tab: 'users' | 'groups';
}) {
  const enabled = useDemoStore((s) => s.enabled);
  if (!enabled) return null;

  const label = busy ? 'Création…' : tab === 'groups' ? 'Remplir démo groupes' : 'Remplir démo';

  return (
    <button
      type="button"
      disabled={busy}
      onClick={onSeed}
      className="flex items-center gap-2 rounded-xl border border-amber-400 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-900 transition hover:bg-amber-100 disabled:opacity-60"
    >
      <svg
        className="h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden
      >
        <title>Remplir démo</title>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
      {label}
    </button>
  );
}

/* ─── Page ──────────────────────────────────────────────── */
export function AdminUsersPage({
  initialTab = 'users',
}: {
  initialTab?: 'users' | 'groups';
} = {}) {
  const role = useAuthStore((s) => s.user?.role);
  const [showCreate, setShowCreate] = useState(false);
  const [adminTab, setAdminTab] = useState<'users' | 'groups'>(initialTab);
  const { seed, busy, result, groupsDemoMessage, error } = useAdminDemoSeed(adminTab);

  if (!role) return null;
  if (role === 'member') return <Navigate to="/" />;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-xl">
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-indigo-800/93">
            Administration
          </p>
          <h2 className="mt-3 text-[2.05rem] font-semibold tracking-tight text-slate-950 sm:text-[2.2rem] sm:leading-tight">
            {adminTab === 'users' ? 'Répertoire des utilisateurs' : 'Groupes de collaborateurs'}
          </h2>
          <p className="mt-[0.975rem] text-[0.9rem] leading-relaxed text-slate-600">
            {adminTab === 'users'
              ? 'Gérez les comptes, affectez les sites de travail et configurez les droits généraux de chaque collaborateur.'
              : 'Constituez des groupes nommés pour affecter des droits spécifiques sur les documents (une personne ou un groupe par règle).'}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <DemoSeedUsersButton onSeed={seed} busy={busy} tab={adminTab} />
          {adminTab === 'users' && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 rounded-2xl bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
                aria-hidden
              >
                <title>Ajouter</title>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Créer un utilisateur
            </button>
          )}
        </div>
      </div>

      {(result || groupsDemoMessage || error) && (
        <div className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 px-4 py-2.5">
          {result && adminTab === 'users' && (
            <span className="text-[0.82rem] font-medium text-emerald-700">
              {result.created} utilisateur{result.created > 1 ? 's' : ''} créé
              {result.created > 1 ? 's' : ''} avec succès.
              {result.skipped > 0 && (
                <span className="ml-1 text-slate-400">
                  ({result.skipped} déjà existant{result.skipped > 1 ? 's' : ''})
                </span>
              )}
            </span>
          )}
          {groupsDemoMessage && adminTab === 'groups' && (
            <span className="text-[0.82rem] font-medium text-emerald-700">{groupsDemoMessage}</span>
          )}
          {error && <span className="text-[0.82rem] text-rose-700">{error}</span>}
        </div>
      )}

      <div className="flex gap-1 rounded-2xl border border-slate-200/80 bg-slate-50/90 p-1">
        <button
          type="button"
          onClick={() => setAdminTab('users')}
          className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
            adminTab === 'users'
              ? 'bg-white text-teal-800 shadow-sm ring-1 ring-slate-200/80'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Utilisateurs
        </button>
        <button
          type="button"
          onClick={() => setAdminTab('groups')}
          className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
            adminTab === 'groups'
              ? 'bg-white text-teal-800 shadow-sm ring-1 ring-slate-200/80'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Groupes
        </button>
      </div>

      {adminTab === 'users' ? <AdminUsersTable /> : <AdminGroupsPanel />}

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
